import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { Link } from "react-router-dom";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ===== Theme ===== */
const UI = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#ffffff",
  subtle: "#f7f7f8",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};
const RAD = { md: "12px", lg: "16px", pill: "999px" };

/* ===== Animations ===== */
const fadeIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;
const spin = keyframes`to{transform:rotate(360deg)}`;

/* ===== Layout ===== */
const PageContainer = styled.div`
  max-width: 1280px;
  margin: 24px auto 36px;
  padding: 0 clamp(10px,4vw,16px);
  display: grid;
  gap: 14px;
`;

const SearchBox = styled.div`
  background: ${UI.card};
  border: 1px solid ${UI.line};
  border-radius: ${RAD.pill};
  padding: 12px 16px;
  box-shadow: ${UI.shadow};
  animation: ${fadeIn} .18s ease;

  input{
    width: 100%;
    border: 0; outline: 0;
    font-size: 1rem; color: ${UI.text};
    background: transparent;
  }
`;

const AlphabetBar = styled.div`
  display: grid;
  grid-template-columns: repeat(27, minmax(0, 1fr)); /* 26 letters + All */
  gap: 8px;
  align-items: center;
  user-select: none;

  @media (max-width: 900px){
    grid-template-columns: repeat(18, minmax(0, 1fr));
  }
  @media (max-width: 640px){
    grid-template-columns: repeat(9, minmax(0, 1fr));
  }
`;

const LetterBtn = styled.button`
  height: 40px;
  border-radius: ${RAD.pill};
  border: 1px solid ${UI.line};
  background: #fff;
  color: ${UI.text};
  font-weight: 800;
  font-size: .92rem;
  cursor: pointer;
  transition: transform .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease;
  box-shadow: ${UI.shadow};

  &:hover{ transform: translateY(-1px); }
  &:active{ transform: translateY(0); }

  ${({$active}) => $active && css`
    background: ${UI.brand};
    border-color: ${UI.brand};
    color: #fff;
  `}
`;

/* ===== Sections ===== */
const SectionsWrap = styled.div`
  display: grid;
  gap: 22px;
  margin-top: 6px;
`;

const Section = styled.section`
  border: 1px solid ${UI.line};
  background: ${UI.card};
  border-radius: ${RAD.lg};
  box-shadow: ${UI.shadow};
  padding: 10px 12px 14px;
  animation: ${fadeIn} .18s ease;
`;

const SectionHeader = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 6px 6px 8px;
  border-bottom: 1px solid ${UI.line};
`;

const LetterBadge = styled.div`
  min-width: 40px; height: 40px; border-radius: ${RAD.pill};
  background: ${UI.brand}; color: #fff; font-weight: 900;
  display: grid; place-items: center; letter-spacing: .2px;
`;

const SectionTitle = styled.h2`
  margin: 0; font-size: 1rem; font-weight: 900; color: ${UI.text};
`;

const BrandList = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0,1fr));
  margin-top: 12px;

  @media (max-width: 1100px){ grid-template-columns: repeat(3, minmax(0,1fr)); }
  @media (max-width: 800px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media (max-width: 480px){ grid-template-columns: 1fr; }
`;

const BrandCard = styled(Link)`
  text-decoration: none; color: inherit;
  border: 1px solid ${UI.line};
  border-radius: ${RAD.md};
  background: #fff;
  padding: 12px;
  transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease;
  min-width: 0;

  &:hover{ box-shadow: ${UI.shadow}; transform: translateY(-2px); border-color: rgba(0,0,0,.12); }
`;

const BrandName = styled.h3`
  margin: 0 0 6px 0;
  font-size: .98rem; font-weight: 900; color: ${UI.text};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: .2px;
`;

const BrandDescription = styled.p`
  margin: 0; color: ${UI.dim}; font-size: .9rem; line-height: 1.5;
`;

/* ===== Empty / Loader ===== */
const LoaderWrap = styled.div`display:grid;place-items:center;padding:60px 0;`;
const Spinner = styled.div`
  width: 42px; height: 42px; border-radius: 50%;
  border: 4px solid rgba(0,0,0,.1); border-top-color: ${UI.brand};
  animation: ${spin} 1s linear infinite;
`;

/* ===== Component ===== */
const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => { document.title = t("titles.brandspage"); }, [language]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const { data } = await api.get(`${API_BASE_URL}/get_brands.php`);
        const sorted = (data || []).sort((a,b)=> (a.name||"").localeCompare(b.name||""));
        setBrands(sorted);
        setFilteredBrands(sorted);
      } catch (e) {
        console.error("Error fetching brands:", e);
      } finally {
        setTimeout(() => setLoading(false), 250);
      }
    };
    fetchBrands();
  }, []);

  // Search & filter (ფუნქციონალი იგივე რჩება)
  useEffect(() => {
    const next = brands.filter(
      (b) =>
        (b.name || "").toLowerCase().includes(searchQuery.toLowerCase()) &&
        (selectedLetter === "" || (b.name || "").toUpperCase().startsWith(selectedLetter))
    );
    setFilteredBrands(next);
  }, [searchQuery, selectedLetter, brands]);

  // Group by first letter
  const grouped = useMemo(() => {
    const buckets = {};
    filteredBrands.forEach((b) => {
      const letter = ((b.name || "").charAt(0) || "#").toUpperCase();
      if (!buckets[letter]) buckets[letter] = [];
      buckets[letter].push(b);
    });
    // keep letters sorted
    return Object.keys(buckets)
      .sort((a,b)=> a.localeCompare(b))
      .map((letter)=> ({ letter, items: buckets[letter] }));
  }, [filteredBrands]);

  if (loading) {
    return (
      <PageContainer>
        <LoaderWrap><Spinner/></LoaderWrap>
      </PageContainer>
    );
  }

  const letters = Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ");
  const descKey = t("brands.description"); // ex: "description_en" | "description_ka"

  return (
    <PageContainer>
      {/* Full-width search on top */}
      <SearchBox>
        <input
          type="text"
          placeholder={t("brands.search")}
          value={searchQuery}
          onChange={(e)=>setSearchQuery(e.target.value)}
          aria-label={t("brands.search")}
        />
      </SearchBox>

      {/* Nicely distributed alphabet */}
      <AlphabetBar>
        {letters.map((L)=>(
          <LetterBtn
            key={L}
            onClick={()=>setSelectedLetter(L)}
            $active={selectedLetter===L}
            aria-pressed={selectedLetter===L}
          >
            {L}
          </LetterBtn>
        ))}
        <LetterBtn
          onClick={()=>setSelectedLetter("")}
          $active={selectedLetter===""}
          aria-pressed={selectedLetter===""}
        >
          All
        </LetterBtn>
      </AlphabetBar>

      {/* Sections by alphabet */}
      <SectionsWrap>
        {grouped.map(({letter, items}) => (
          <Section key={letter} id={`letter-${letter}`}>
            <SectionHeader>
              <LetterBadge>{letter}</LetterBadge>
              <SectionTitle>
                {letter} • {items.length}
              </SectionTitle>
            </SectionHeader>

            <BrandList>
              {items.map((brand) => {
                const raw = (brand?.[descKey] || "").toString();
                const shortDesc = raw.length > 90 ? `${raw.slice(0, 90)}…` : raw;

                return (
                  <BrandCard
                    to={`/brands/${(brand.name || "").replace(/\s+/g, "-")}`}
                    key={brand.id || brand.name}
                    title={brand.name}
                  >
                    <BrandName>{brand.name}</BrandName>
                    <BrandDescription>{shortDesc}</BrandDescription>
                  </BrandCard>
                );
              })}
            </BrandList>
          </Section>
        ))}
      </SectionsWrap>
    </PageContainer>
  );
};

export default BrandsPage;
